import { Form, Input, Modal, Radio, Space, Typography } from 'antd';
import isEqual from 'lodash/isEqual';
import {
  type FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  type ModelSmsServiceStatisticsTotals,
  type ModelSmsServiceStatsCounters,
  useAddIntegrationInOneStepMutation,
  useAddIntegrationMutation,
  useEditOneStepSocialIntegrationMutation,
  useGetSmsServiceStatisticsQuery,
  useGetSocialIntegrationOperatorsQuery,
  useSetSocialIntegrationOperatorsMutation,
} from '@app/api/SocialIntegrations';
import {
  CANCEL,
  ERROR_REQUIRED_FIELD,
  FRONT_WARNING,
  INSTAGRAM,
  PREFIX,
  SAVE,
  SUCCESS,
  TELEGRAM,
  TELEGRAM_PERSONAL,
  TELEGRAM_PERSONAL_LABEL,
  MAX_MESSENGER,
  MAX_MESSENGER_LABEL,
  SMS_SERVICE,
  SMS_SERVICE_LABEL,
  TELEGRAM_LABEL,
  VK,
  VK_LABEL,
  WHATSAPP,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useConfirm } from '@app/hooks/useConfirm';
import { IntegrationsContext, NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';

import { OperatorsBlock } from '@pages/Integrations/IntegrationsList/components/OperatorsBlock';
import { SalesFunnelFormItems } from '@pages/Integrations/IntegrationsList/components/SalesFunnelsFormItems';

import type {
  AddInstagramIntegrationModalForm,
  AddInstagramIntegrationModalProps,
} from '@pages/Integrations/IntegrationsList/types';

import type { DefaultOptionType } from 'antd/es/select';

const { Text } = Typography;
const { useForm } = Form;

// Создание интеграции ходит во внешние сервисы и может отвечать минутами —
// после этого порога объясняем фоновое продолжение. Само закрытие во время
// запроса всё равно требует явного подтверждения ниже.
const SLOW_RESPONSE_SECONDS = 30;

const formatSmsCounters = (counters: ModelSmsServiceStatsCounters) =>
  `отправлено ${counters.SubmittedToModem}, ошибок ${
    counters.Failed
  }, в обработке ${counters.Leased + counters.Accepted}`;

// Ошибками по итогам считаем не только Failed, но и просроченные попытки
// (LeaseExpired, Expired) — это поля есть только в тоталах, не в разрезе устройств/сим.
const formatSmsTotals = (totals: ModelSmsServiceStatisticsTotals) =>
  `отправлено ${totals.SubmittedToModem}, ошибок ${
    totals.Failed + totals.LeaseExpired + totals.Expired
  }, в обработке ${totals.Leased + totals.Accepted}`;

export const SocialMediaSettingsModal: FC<
  AddInstagramIntegrationModalProps
> = ({
  getListData,
  salesFunnels,
  salesFunnelsStages,
  setSelectedSalesFunnelId,
  createHubConnection,
  openWhatsAppAuthorization,
  setInitialSalesFunnel,
  editableIntegration,
  setEditableIntegration,
  openConnectAuth,
}) => {
  const { activeBranchId } = useUserPrint();
  const { show: showConfirm } = useConfirm();

  const [isModalLoading, setIsModalLoading] = useState(false);

  const {
    isSocialMediaSettingsModalOpen,
    setIsSocialMediaSettingsModalOpen,
    currentIntegrationType,
    setIsWhatsAppModalOpen,
  } = useContext(IntegrationsContext);

  const { showNotification } = useContext(NotificationContext);

  const [form] = useForm();

  const [addIntegration] = useAddIntegrationMutation();
  const [addIntegrationInOneStep] = useAddIntegrationInOneStepMutation();
  const [editOneStepSocialIntegration] =
    useEditOneStepSocialIntegrationMutation();
  const [editOperators] = useSetSocialIntegrationOperatorsMutation();

  const {
    data: operatorsData,
    isLoading: isOperatorsLoading,
    isFetching: isOperatorsFetching,
  } = useGetSocialIntegrationOperatorsQuery(
    {
      ActiveBranchId: activeBranchId ?? 0,
      integrationId: editableIntegration?.Id ?? 0,
    },
    {
      skip: activeBranchId === null || !editableIntegration?.Id,
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: smsStatsData, isFetching: isSmsStatsFetching } =
    useGetSmsServiceStatisticsQuery(
      {
        ActiveBranchId: activeBranchId ?? 0,
        integrationId: editableIntegration?.Id ?? 0,
      },
      {
        skip:
          activeBranchId === null ||
          !editableIntegration?.Id ||
          currentIntegrationType !== SMS_SERVICE,
        refetchOnMountOrArgChange: true,
      }
    );

  const operatorsOptions: Array<DefaultOptionType> = useMemo(() => {
    if (isOperatorsLoading || isOperatorsFetching || !operatorsData) {
      return [];
    }

    return operatorsData.map(({ Id, NameFirst, NameLast }) => ({
      value: Id,
      label: `${NameFirst} ${NameLast}`,
    }));
  }, [operatorsData, isOperatorsFetching, isOperatorsLoading]);

  const slowResponseTimerRef = useRef<number | null>(null);
  const isSubmittingRef = useRef(false);
  const isSubmissionBackgroundedRef = useRef(false);

  const clearSlowResponseTimer = useCallback(() => {
    if (slowResponseTimerRef.current !== null) {
      window.clearTimeout(slowResponseTimerRef.current);
      slowResponseTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearSlowResponseTimer();
    };
  }, [clearSlowResponseTimer]);

  useEffect(() => {
    if (!isSocialMediaSettingsModalOpen || !editableIntegration) return;

    form.setFieldsValue({
      Name: editableIntegration.SocialIntegrationName ?? undefined,
      salesFunnel: editableIntegration.SalesFunnelId ?? undefined,
      salesFunnelStage: editableIntegration.SalesFunnelStageId ?? undefined,
    });

    if (editableIntegration.SalesFunnelId) {
      setSelectedSalesFunnelId(editableIntegration.SalesFunnelId);
    }
  }, [
    isSocialMediaSettingsModalOpen,
    editableIntegration,
    form,
    setSelectedSalesFunnelId,
  ]);

  const initOperators = useCallback(() => {
    if (isOperatorsLoading || isOperatorsFetching || !operatorsData) {
      return;
    }

    const selectedOperators = operatorsData.filter(
      ({ IsSelected }) => IsSelected
    );

    form.setFieldsValue({
      operators: selectedOperators.map(({ Id }) => Id),
    });
  }, [operatorsData, isOperatorsFetching, isOperatorsLoading, form]);

  useEffect(() => {
    initOperators();
  }, [initOperators]);

  const editOperatorsHandler = useCallback(
    async (operators: Array<number> | undefined) => {
      if (!activeBranchId || !editableIntegration) return;

      const selectedOperators =
        operatorsData?.filter(({ IsSelected }) => IsSelected) ?? [];
      const currentOperators = selectedOperators.map(({ Id }) => Id);

      const noChangedOperators = isEqual(currentOperators, operators ?? []);

      if (noChangedOperators) return;

      try {
        await editOperators({
          ActiveBranchId: activeBranchId,
          data: {
            IntegrationId: editableIntegration.Id,
            OperatorIds: (operators ?? []).filter(
              (id) => typeof id === 'number'
            ),
          },
        }).unwrap();

        showNotification('Операторы обновлены успешно', SUCCESS);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [
      activeBranchId,
      editableIntegration,
      operatorsData,
      showNotification,
      editOperators,
    ]
  );

  const closeModal = useCallback(() => {
    setIsSocialMediaSettingsModalOpen(false);
    setEditableIntegration(undefined);
  }, [setIsSocialMediaSettingsModalOpen, setEditableIntegration]);

  const handleCancel = useCallback(() => {
    if (!isSubmittingRef.current) {
      closeModal();

      return;
    }

    showConfirm({
      title: 'Закрыть создание интеграции?',
      subtitle:
        'Запрос уже отправлен. Создание продолжится в фоне, а результат появится в списке интеграций. Не отправляйте ту же форму повторно.',
      confirmText: 'Закрыть',
      cancelText: 'Продолжить ожидание',
      onConfirm: () => {
        isSubmissionBackgroundedRef.current = true;
        closeModal();
      },
    });
  }, [closeModal, showConfirm]);

  const handleSalesFunnelChange = (value: number) => {
    setSelectedSalesFunnelId(value);
    form.setFieldsValue({
      salesFunnelStage: null,
    });
  };

  const handleSubmitForm = async (values: AddInstagramIntegrationModalForm) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    isSubmissionBackgroundedRef.current = false;

    const {
      Name,
      HistoryUploadPeriod,
      salesFunnel,
      salesFunnelStage,
      operators,
    } = values;

    try {
      setIsModalLoading(true);

      slowResponseTimerRef.current = window.setTimeout(() => {
        showNotification(
          'Сервис долго не отвечает. Окно можно закрыть — интеграция появится в списке после завершения обработки',
          FRONT_WARNING
        );
      }, SLOW_RESPONSE_SECONDS * 1000);

      if (editableIntegration) {
        await editOneStepSocialIntegration({
          ActiveBranchId: activeBranchId,
          data: {
            IntegrationId: editableIntegration.Id,
            Name: Name ?? null,
            SalesFunnelId: salesFunnel,
            SalesFunnelStageId: salesFunnelStage,
            ParseUsingNeural: editableIntegration.ParseUsingNeural ?? false,
          },
        }).unwrap();

        showNotification('Интеграция обновлена успешно', SUCCESS);

        await editOperatorsHandler(operators);

        if (!isSubmissionBackgroundedRef.current) {
          closeModal();
        }

        return;
      }

      const result =
        currentIntegrationType === SMS_SERVICE
          ? await addIntegrationInOneStep({
              ActiveBranchId: activeBranchId,
              socialType: currentIntegrationType,
              data: {
                Name: Name ?? null,
                HistoryUploadPeriod,
                SalesFunnelId: salesFunnel,
                SalesFunnelStageId: salesFunnelStage,
              },
            }).unwrap()
          : await addIntegration({
              ActiveBranchId: activeBranchId,
              socialType: currentIntegrationType,
              data: {
                Name: Name ?? null,
                HistoryUploadPeriod,
                SalesFunnelId: salesFunnel,
                SalesFunnelStageId: salesFunnelStage,
              },
            }).unwrap();

      // Вход Telegram (личный и бот), MAX, ВКонтакте и Instagram идёт через
      // единое окно подключения (us/10721) — OAuth-фрейм этим каналам не нужен.
      if (
        [TELEGRAM, TELEGRAM_PERSONAL, MAX_MESSENGER, VK, INSTAGRAM].includes(
          currentIntegrationType
        )
      ) {
        if (!isSubmissionBackgroundedRef.current) {
          closeModal();
          openConnectAuth(
            currentIntegrationType,
            result.IntegrationId,
            result.OAuthUrl
          );
        }
        return;
      }

      // Новая WhatsApp-интеграция входит в ту же сессию авторизации, что и
      // повторный вход: окно само опрашивает состояние и показывает успех.
      // Раньше здесь жил собственный интервал обновления QR, которым владел
      // этот компонент, а закрытие по успеху делал родитель — остановить чужой
      // интервал он не мог (issue/11029).
      if (currentIntegrationType === WHATSAPP) {
        if (!isSubmissionBackgroundedRef.current) {
          setIsWhatsAppModalOpen(true);
          openWhatsAppAuthorization(result);
          // Сессией QR теперь полностью владеет родительский hook. Форму
          // создания под ней не сохраняем: повторный submit создавал дубликат.
          closeModal();
        }

        return;
      }

      // default case
      createHubConnection(result);
      if (!isSubmissionBackgroundedRef.current) {
        closeModal();
      }
    } catch (error) {
      showNotification(handleCatchError(error));
      // Для WhatsApp заполненную форму оставляем для явного повтора. У
      // остальных каналов сохраняем прежнее поведение закрытия после ошибки.
      if (
        !isSubmissionBackgroundedRef.current &&
        (editableIntegration || currentIntegrationType !== WHATSAPP)
      ) {
        closeModal();
      }
    } finally {
      clearSlowResponseTimer();
      isSubmittingRef.current = false;

      await getListData();
      setIsModalLoading(false);
    }
  };

  const getModalTitle = () => {
    switch (currentIntegrationType) {
      case INSTAGRAM:
        return 'Instagram';
      case WHATSAPP:
        return 'WhatsApp';
      case TELEGRAM_PERSONAL:
        return TELEGRAM_PERSONAL_LABEL;
      case MAX_MESSENGER:
        return MAX_MESSENGER_LABEL;
      case SMS_SERVICE:
        return SMS_SERVICE_LABEL;
      case TELEGRAM:
        return TELEGRAM_LABEL;
      case VK:
        return VK_LABEL;
      default:
        return '';
    }
  };

  return (
    <Modal
      open={isSocialMediaSettingsModalOpen}
      onCancel={handleCancel}
      title={`${
        editableIntegration ? 'Редактировать' : 'Добавить'
      } интеграцию ${getModalTitle()}`}
      okText={SAVE}
      cancelText={CANCEL}
      className={`${PREFIX}add-instagram-integration-modal`}
      onOk={form.submit}
      okButtonProps={{
        loading: isModalLoading,
      }}
      afterClose={() => {
        // Форму можно очистить, но незавершённый background submit всё ещё
        // должен держать кнопку в loading: иначе повторно открытая модалка
        // выглядела готовой, хотя guard молча отклонял каждый новый клик.
        form.resetFields();
        if (!isSubmittingRef.current) {
          setIsModalLoading(false);
        }
      }}
    >
      <Form
        form={form}
        onFinish={handleSubmitForm}
        initialValues={{
          HistoryUploadPeriod: 4,
          salesFunnel: setInitialSalesFunnel(),
        }}
      >
        <div className="container">
          <div className="item-container">
            <Text strong className="label">
              Название интеграции
            </Text>
            <Form.Item
              name="Name"
              rules={[
                {
                  max: 140,
                  min: 1,
                  message: 'Название должно быть от 1 до 140 символов',
                },
                { required: true, message: ERROR_REQUIRED_FIELD },
              ]}
            >
              <Input size="large" />
            </Form.Item>
          </div>

          {Boolean(editableIntegration) &&
            currentIntegrationType === SMS_SERVICE && (
              <div className="item-container">
                <Text strong className="label">
                  Токен
                </Text>
                <Input
                  size="large"
                  readOnly
                  value={editableIntegration?.Token ?? ''}
                />
              </div>
            )}

          {!editableIntegration && (
            <div className="item-container">
              <Text strong className="label">
                Загрузка сообщений чата
              </Text>
              <Form.Item name="HistoryUploadPeriod">
                <Radio.Group>
                  <Space direction="vertical">
                    <Radio value={1}>Все</Radio>
                    <Radio value={2}>За неделю</Radio>
                    <Radio value={3}>Сегодняшние</Radio>
                    <Radio value={4}>Не загружать</Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>
            </div>
          )}
        </div>

        {Boolean(editableIntegration) &&
          currentIntegrationType === SMS_SERVICE && (
            <div className="item-container">
              <Text strong className="label">
                Операторы
              </Text>
              <OperatorsBlock
                operators={operatorsOptions}
                isLoading={isOperatorsLoading || isOperatorsFetching}
              />
            </div>
          )}

        {Boolean(editableIntegration) &&
          currentIntegrationType === SMS_SERVICE && (
            <div className="item-container">
              <Text strong className="label">
                Статистика отправки SMS
              </Text>
              {isSmsStatsFetching ? (
                <Text type="secondary">Загрузка...</Text>
              ) : (
                <div>
                  {smsStatsData && (
                    <div>
                      <Text strong>Всего</Text>:{' '}
                      {formatSmsTotals(smsStatsData.Totals)}
                    </div>
                  )}
                  {smsStatsData?.Devices?.map((device) => (
                    <div key={device.DeviceId}>
                      <Text strong>
                        {device.DisplayName || device.DeviceId}
                      </Text>
                      : {formatSmsCounters(device)}
                      {device.Sims?.map((sim) => (
                        <div key={sim.SimId} className="sms-stats-sim">
                          {sim.SenderPhone || sim.SimId}:{' '}
                          {formatSmsCounters(sim)}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        <SalesFunnelFormItems
          salesFunnels={salesFunnels}
          salesFunnelsStages={salesFunnelsStages}
          onSalesFunnelChange={handleSalesFunnelChange}
          errorRequiredField={ERROR_REQUIRED_FIELD}
          isIntegrationRegistryComponent={true}
        />
      </Form>
    </Modal>
  );
};
